/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, UploadCloud, FileText, X, AlertTriangle
} from 'lucide-react';
import {
  knowledgeBaseService,
  AI_AGENT_ERROR_CODES,
  getApiErrorMessage,
  KB_DOCUMENT_MAX_SIZE_BYTES,
} from '../services/knowledgeBaseService';
import useDocumentMetadataForm from '../hooks/useDocumentMetadataForm';
import DocumentMetadataFields, { MetadataSectionHeader } from '../components/knowledgeBases/DocumentMetadataFields';
import toastService from '../utils/toastService';
import { useAuth } from '../context/AuthContext';
import { ACCOUNT_LABEL } from '../constants/entityLabels';

const LIST_ROUTE = '/knowledge-bases';
const PDF_MIME = 'application/pdf';

const formatFileSize = (bytes) => {
  if (bytes < 1024) { return `${bytes} B`; }
  if (bytes < 1024 * 1024) { return `${(bytes / 1024).toFixed(1)} KB`; }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const validateFile = (file) => {
  if (!file) { return 'A PDF file is required'; }
  const isPdf = file.type === PDF_MIME && file.name.toLowerCase().endsWith('.pdf');
  if (!isPdf) { return 'Only PDF files are allowed'; }
  if (file.size > KB_DOCUMENT_MAX_SIZE_BYTES) {
    return `File size must not exceed ${KB_DOCUMENT_MAX_SIZE_BYTES / (1024 * 1024)}MB`;
  }
  return null;
};

const UploadKnowledgeBaseDocument = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: knowledgeBaseId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  // Platform-wide view carries the account in the URL; account / sub-account users use their own.
  const showAccountField = !user?.accountUID;
  const accountId = showAccountField ? (searchParams.get('accountId') || '') : user?.accountUID;
  const knowledgeBaseName = location.state?.knowledgeBase?.name;

  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [fileError, setFileError] = useState(null);
  const [integrationError, setIntegrationError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const form = useDocumentMetadataForm(accountId);

  // Back / cancel / success all return to this knowledge base's documents list.
  const goToDocuments = () => {
    const query = showAccountField && accountId ? `?accountId=${encodeURIComponent(accountId)}` : '';
    navigate(`${LIST_ROUTE}/${encodeURIComponent(knowledgeBaseId)}/documents${query}`, {
      state: { knowledgeBase: { id: knowledgeBaseId, name: knowledgeBaseName } },
    });
  };

  useEffect(() => {
    if (user && showAccountField && !accountId) {
      toastService.error(`Select ${ACCOUNT_LABEL.withArticle} first`);
      navigate(LIST_ROUTE, { replace: true });
    }
  }, [user, showAccountField, accountId, navigate]);

  const handleFileSelected = (selected) => {
    if (!selected) { return; }
    const error = validateFile(selected);
    setFileError(error);
    setFile(error ? null : selected);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelected(e.dataTransfer.files?.[0]);
  };

  const clearFile = () => {
    setFile(null);
    if (fileInputRef.current) { fileInputRef.current.value = ''; }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextFileError = validateFile(file);
    setFileError(nextFileError);
    const pairsValid = form.validate();
    if (nextFileError || !pairsValid) { return; }

    const formData = new FormData();
    formData.append('file', file);
    if (showAccountField) { formData.append('accountId', accountId); }
    if (form.typeUid) { formData.append('serviceRequestTypeUid', form.typeUid); }
    if (form.leafUid) { formData.append('hierarchyNodeUid', form.leafUid); }
    if (Object.keys(form.customMetadata).length > 0) {
      formData.append('metadata', JSON.stringify(form.customMetadata));
    }

    try {
      setSubmitting(true);
      setUploadProgress(0);
      setIntegrationError(null);
      await knowledgeBaseService.uploadDocument(knowledgeBaseId, formData, {
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
          }
        },
      });
      toastService.success('Document uploaded successfully');
      goToDocuments();
    } catch (err) {
      const message = getApiErrorMessage(err, 'Failed to upload document');
      if (AI_AGENT_ERROR_CODES.includes(err.response?.data?.code)) {
        setIntegrationError(message);
      }
      toastService.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 p-1 sm:p-2 w-full pb-12">
      {/* Top Breadcrumb */}
      <div>
        <button
          type="button"
          onClick={goToDocuments}
          className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-base-content/50 hover:text-primary transition-colors group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          Back to Documents
        </button>
      </div>

      {/* Hero Header Banner */}
      <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10">
        <div className="space-y-1">
          <span className="text-[10px] font-black uppercase tracking-widest text-base-content/40">
            Upload Document
          </span>
          <h1 className="text-2xl sm:text-3xl font-black text-base-content tracking-tight break-words">
            {knowledgeBaseName || 'Knowledge Base'}
          </h1>
          <p className="text-xs text-base-content/50">
            Upload a PDF to this knowledge base and tag it with metadata for the AI Agent.
          </p>
        </div>
      </div>

      {integrationError && (
        <div role="alert" className="alert alert-warning rounded-xl text-sm font-semibold">
          <AlertTriangle size={18} className="shrink-0" />
          <span>{integrationError}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Document */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
          <MetadataSectionHeader icon={FileText} title="Document" badge="Required" />

          <input
            ref={fileInputRef}
            id="document"
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(e) => handleFileSelected(e.target.files?.[0])}
          />

          {file ? (
            <div className="flex items-center justify-between gap-3 p-4 rounded-xl bg-base-200/50 border border-base-content/10">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-error/10 text-error flex items-center justify-center shrink-0">
                  <FileText size={20} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-base-content truncate">{file.name}</p>
                  <p className="text-xs text-base-content/50">{formatFileSize(file.size)}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={clearFile}
                disabled={submitting}
                className="p-1.5 text-base-content/40 hover:text-error hover:bg-error/10 rounded-lg transition-all"
                aria-label="Remove file"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <label
              htmlFor="document"
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              className={`flex flex-col items-center justify-center gap-2 px-6 py-10 rounded-xl border-2 border-dashed cursor-pointer transition-all text-center ${
                isDragging
                  ? 'border-primary bg-primary/5'
                  : fileError ? 'border-error' : 'border-base-300 hover:border-primary/50 hover:bg-base-200/40'
              }`}
            >
              <UploadCloud size={32} className="text-primary" />
              <span className="text-sm font-bold text-base-content">Click to choose or drag a PDF here</span>
              <span className="text-xs text-base-content/40">
                PDF only · up to {KB_DOCUMENT_MAX_SIZE_BYTES / (1024 * 1024)}MB
              </span>
            </label>
          )}
          {fileError && <p className="text-error text-xs font-medium">{fileError}</p>}
        </div>

        <DocumentMetadataFields form={form} disabled={submitting} />

        {/* Form Actions */}
        <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-2">
          <button
            type="button"
            onClick={goToDocuments}
            className="w-full sm:w-auto px-6 py-3 border border-base-content/20 text-base-content/70 hover:bg-base-200 rounded-xl font-bold text-sm transition-all"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="w-full sm:flex-1 btn-premium justify-center"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-primary-content/30 border-t-primary-content rounded-full animate-spin"></div>
                <span>{uploadProgress < 100 ? `Uploading ${uploadProgress}%...` : 'Processing...'}</span>
              </>
            ) : (
              <>
                <UploadCloud size={16} />
                <span>Upload Document</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UploadKnowledgeBaseDocument;
